import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileText, Upload, CloudUpload, CheckCircle2, AlertCircle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { reportGroupService } from '@/integrations/supabase/reportGroupService';
import { timePeriodService } from '@/integrations/supabase/periodService';
import { supabase } from '@/integrations/supabase/client';
import { useDriveUpload } from '@/hooks/useDriveUpload';
import { useAuth } from '@/components/AuthProvider';
import { useCurrentUserProfile } from '@/hooks/useSupabaseUsers';

const BOD_EMAIL = 'ytrinh@eco-tech.vn';

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.html,.htm';
const MAX_SIZE_MB = 25;

/** Chuyển tên file thành tên báo cáo: bỏ extension, thay dấu gạch bằng khoảng trắng, trim. */
function fileNameToReportName(filename: string): string {
  return filename
    .replace(/\.[^.]+$/, '')           // bỏ extension
    .replace(/[-_]+/g, ' ')            // gạch ngang/gạch dưới → khoảng trắng
    .replace(/\s{2,}/g, ' ')           // nhiều space → 1 space
    .trim();
}

// Parse period ID to human-readable label. E.g. W2613 → "W2613 — Tuần 13, 2026"
function formatPeriodLabel(id: string): string {
  if (!id || id.length < 4) return id;
  const type = id[0];
  const yearSuffix = id.substring(1, 3);
  const rest = id.substring(3);
  const year = 2000 + parseInt(yearSuffix, 10);

  if (type === 'W') return `${id} — Tuần ${parseInt(rest, 10)}, ${year}`;
  if (type === 'M') return `${id} — Tháng ${parseInt(rest, 10)}, ${year}`;
  if (type === 'Q') return `${id} — Quý ${rest}, ${year}`;
  return id;
}

/**
 * Calculate ISO week number and ISO week-year for a given date.
 * Matches Excel WEEKNUM(date, 21): week starts Monday, week 1 contains first Thursday.
 */
function getISOWeekInfo(date: Date): { week: number; year: number } {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // Shift to the nearest Thursday to get the correct ISO year
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const year = d.getFullYear();
  const yearStart = new Date(year, 0, 1);
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { week, year };
}

/** Return period ID (e.g. "W2613") for a date offset by `weekOffset` weeks from `base`. */
function getPeriodIdForWeekOffset(base: Date, weekOffset: number): string {
  const d = new Date(base);
  d.setDate(d.getDate() + weekOffset * 7);
  const { week, year } = getISOWeekInfo(d);
  const yy = String(year % 100).padStart(2, '0');
  const ww = String(week).padStart(2, '0');
  return `W${yy}${ww}`;
}

const TODAY = new Date();
const CURRENT_PERIOD_ID = getPeriodIdForWeekOffset(TODAY, 0);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultGroupId?: string;
}

export function CreateReportDialog({ open, onOpenChange, defaultGroupId }: Props) {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile();
  const { uploadReport, isUploading, progress, stage, error, reset } = useDriveUpload();
  const isBOD = user?.email === BOD_EMAIL;

  const [reportName, setReportName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(defaultGroupId || '');
  const [selectedPeriod, setSelectedPeriod] = useState(CURRENT_PERIOD_ID);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState('');
  const [isSummary, setIsSummary] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearFieldError = (key: string) => {
    setFieldErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Fetch report groups
  const { data: groupsData } = useQuery({
    queryKey: ['report-groups-for-create'],
    queryFn: () => reportGroupService.getReportGroups({}, { limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  // When dialog opens, sync the selected group to defaultGroupId — nhưng chỉ
  // khi danh sách nhóm đã nạp. Select nằm trong <form>: set value lúc option
  // còn rỗng sẽ bị BubbleSelect của Radix ép về '' (xem EditReportDialog).
  const groupOptionsReady = !!groupsData;
  useEffect(() => {
    if (open && groupOptionsReady) {
      setSelectedGroup(defaultGroupId || '');
      // Kỳ mặc định (tuần hiện tại) cũng bị cơ chế trên xoá ở lần mở đầu —
      // khôi phục nếu đang rỗng, không đụng lựa chọn người dùng đã đổi.
      setSelectedPeriod(p => p || CURRENT_PERIOD_ID);
    }
  }, [open, defaultGroupId, groupOptionsReady]);

  // Fetch 5 periods: current week ±2 (ISO week, matches Excel WEEKNUM mode 21)
  const { data: periods } = useQuery({
    queryKey: ['time-periods-for-create'],
    queryFn: () => timePeriodService.getTimePeriods(),
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      const targetIds = new Set(
        [-2, -1, 0, 1, 2].map(offset => getPeriodIdForWeekOffset(TODAY, offset))
      );
      return data
        .filter(p => targetIds.has(p.id))
        .sort((a, b) => b.id.localeCompare(a.id));
    },
  });

  // Fetch teams
  const { data: teamsData } = useQuery({
    queryKey: ['teams-for-create'],
    queryFn: async () => {
      const { data } = await supabase
        .from('teams')
        .select('legacy_id, team_name')
        .order('team_name')
        .limit(100);
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFileError('');
    clearFieldError('file');
    if (!f) { setFile(null); return; }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      setFileError(`File vượt quá ${MAX_SIZE_MB}MB`);
      setFile(null);
      return;
    }
    setFile(f);
    // Tự động điền tên báo cáo từ tên file (nếu người dùng chưa nhập gì)
    if (!reportName.trim()) {
      setReportName(fileNameToReportName(f.name));
      clearFieldError('reportName');
    }
  };

  const validateRequired = (): boolean => {
    const next: Record<string, string> = {};
    if (!reportName.trim()) next.reportName = 'Vui lòng nhập tên báo cáo';
    if (!selectedGroup) next.group = 'Vui lòng chọn nhóm báo cáo';
    if (!selectedPeriod) next.period = 'Vui lòng chọn kỳ báo cáo';
    if (!selectedTeam) next.team = 'Vui lòng chọn team';
    if (!reportDate) next.reportDate = 'Vui lòng chọn ngày báo cáo';
    if (!file) {
      next.file = 'Vui lòng chọn file báo cáo';
      setFileError('Vui lòng chọn file báo cáo');
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateRequired()) return;

    const result = await uploadReport({
      file,
      metadata: {
        report_name: reportName.trim(),
        id_group: selectedGroup,
        id_team: selectedTeam,
        period: selectedPeriod,
        report_date: reportDate,
        summary: summary.trim() || undefined,
        auth_user_id: user?.id,
        user_id: user?.id,
        created_by_user_id: profile?.legacy_id ?? undefined,
        is_summary: isBOD ? isSummary : undefined,
      },
    });

    if (result?.success) {
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 1800);
    }
  };

  const handleClose = () => {
    if (isUploading) return;
    setReportName('');
    setSelectedGroup(defaultGroupId || '');
    setSelectedPeriod(CURRENT_PERIOD_ID);
    setSelectedTeam('');
    setReportDate(new Date().toISOString().split('T')[0]);
    setSummary('');
    setIsSummary(false);
    setFile(null);
    setFileError('');
    setFieldErrors({});
    setSuccess(false);
    reset();
    onOpenChange(false);
  };

  const stageLabel = stage === 'staging'
    ? 'Đang tải file lên...'
    : stage === 'processing'
    ? 'Đang lưu vào kho báo cáo...'
    : 'Hoàn tất';

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="flex max-h-[85dvh] w-full max-w-lg flex-col gap-0 overflow-hidden p-0 sm:max-w-[32rem]">
        <div className="shrink-0 border-b px-6 pb-4 pt-6">
          <DialogHeader className="space-y-0">
            <DialogTitle className="flex items-center gap-2 pr-8">
              <CloudUpload className="h-5 w-5 shrink-0" />
              Tạo báo cáo mới
            </DialogTitle>
          </DialogHeader>
        </div>

        {success ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-8">
              <div className="flex flex-col items-center gap-3 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
                <p className="text-lg font-semibold">Tạo báo cáo thành công!</p>
                <p className="text-sm text-muted-foreground">File đã được lưu vào kho báo cáo.</p>
              </div>
            </div>
            <DialogFooter className="shrink-0 gap-2 border-t bg-background px-6 py-4">
              <Button type="button" className="w-full sm:w-auto" onClick={handleClose}>
                Đóng
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {/* File upload — đầu tiên */}
                <div className="space-y-1">
                  <Label>File báo cáo *</Label>
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (!isUploading && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    className={`cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors hover:border-primary/60 hover:bg-accent/30 ${
                      fieldErrors.file ? 'border-destructive/60' : ''
                    }`}
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                  >
                    {file ? (
                      <div className="flex items-center justify-center gap-2 text-sm">
                        <FileText className="h-4 w-4 shrink-0 text-primary" />
                        <span className="max-w-[16rem] truncate font-medium">{file.name}</span>
                        <span className="text-muted-foreground">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-muted-foreground">
                        <Upload className="h-6 w-6" />
                        <span className="text-sm">Bấm để chọn file (tối đa {MAX_SIZE_MB}MB)</span>
                        <span className="text-xs">.pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .html</span>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                  {(fileError || fieldErrors.file) && (
                    <p className="text-sm text-destructive">{fileError || fieldErrors.file}</p>
                  )}
                </div>

                {/* Report name — tự động điền từ tên file */}
                <div className="space-y-1">
                  <Label htmlFor="report_name">Tên báo cáo *</Label>
                  <Input
                    id="report_name"
                    value={reportName}
                    onChange={e => {
                      setReportName(e.target.value);
                      clearFieldError('reportName');
                    }}
                    placeholder="Chọn file để tự động điền tên..."
                    required
                    aria-invalid={!!fieldErrors.reportName}
                    disabled={isUploading}
                  />
                  {fieldErrors.reportName && (
                    <p className="text-sm text-destructive">{fieldErrors.reportName}</p>
                  )}
                </div>

                {/* Group + Period in 2 cols */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Nhóm báo cáo *</Label>
                    <Select
                      value={selectedGroup || undefined}
                      onValueChange={v => {
                        setSelectedGroup(v);
                        clearFieldError('group');
                      }}
                      disabled={isUploading}
                      required
                    >
                      <SelectTrigger aria-invalid={!!fieldErrors.group}>
                        <SelectValue placeholder="Chọn nhóm..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-56">
                        {groupsData?.data.map(g => (
                          <SelectItem key={g.legacy_id} value={g.legacy_id}>
                            {g.group_name || g.legacy_id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.group && <p className="text-sm text-destructive">{fieldErrors.group}</p>}
                  </div>

                  <div className="space-y-1">
                    <Label>Kỳ báo cáo *</Label>
                    <Select
                      value={selectedPeriod}
                      onValueChange={v => {
                        setSelectedPeriod(v);
                        clearFieldError('period');
                      }}
                      disabled={isUploading}
                      required
                    >
                      <SelectTrigger aria-invalid={!!fieldErrors.period}>
                        <SelectValue placeholder="Chọn kỳ..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {periods?.map(p => {
                          const label = formatPeriodLabel(p.id);
                          return (
                            <SelectItem key={p.id} value={p.id}>
                              {label}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {fieldErrors.period && <p className="text-sm text-destructive">{fieldErrors.period}</p>}
                  </div>
                </div>

                {/* Team + Date in 2 cols */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Team *</Label>
                    <Select
                      value={selectedTeam || undefined}
                      onValueChange={v => {
                        setSelectedTeam(v);
                        clearFieldError('team');
                      }}
                      disabled={isUploading}
                      required
                    >
                      <SelectTrigger aria-invalid={!!fieldErrors.team}>
                        <SelectValue placeholder="Chọn team..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-56">
                        {teamsData?.map(t => (
                          <SelectItem key={t.legacy_id} value={t.legacy_id}>
                            {t.team_name || t.legacy_id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.team && <p className="text-sm text-destructive">{fieldErrors.team}</p>}
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="report_date">Ngày báo cáo *</Label>
                    <Input
                      id="report_date"
                      type="date"
                      value={reportDate}
                      onChange={e => {
                        setReportDate(e.target.value);
                        clearFieldError('reportDate');
                      }}
                      required
                      aria-invalid={!!fieldErrors.reportDate}
                      disabled={isUploading}
                    />
                    {fieldErrors.reportDate && (
                      <p className="text-sm text-destructive">{fieldErrors.reportDate}</p>
                    )}
                  </div>
                </div>

                {/* Summary — optional */}
                <div className="space-y-1">
                  <Label htmlFor="summary">Tóm tắt nội dung (tùy chọn)</Label>
                  <Textarea
                    id="summary"
                    value={summary}
                    onChange={e => setSummary(e.target.value)}
                    placeholder="Mô tả ngắn về nội dung báo cáo..."
                    rows={3}
                    disabled={isUploading}
                  />
                </div>

                {/* Summary report checkbox - BOD only */}
                {isBOD && (
                  <div className="flex items-center gap-2 rounded-md border bg-amber-50/60 px-3 py-2.5">
                    <Checkbox
                      id="is_summary"
                      checked={isSummary}
                      onCheckedChange={(v) => setIsSummary(v === true)}
                      disabled={isUploading}
                    />
                    <Label htmlFor="is_summary" className="cursor-pointer text-sm font-medium text-amber-800">
                      Đánh dấu là Báo cáo tổng hợp (Summary Report)
                    </Label>
                  </div>
                )}

              </div>
            </div>

            <DialogFooter className="shrink-0 flex-col gap-2 border-t bg-background px-6 py-4">
              {/* Progress — chỉ hiện khi đang upload, nằm trong footer */}
              {isUploading && (
                <div className="w-full space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{stageLabel}</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-1.5" />
                </div>
              )}

              {/* Error */}
              {error && !isUploading && (
                <Alert variant="destructive" className="w-full py-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex w-full justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleClose} disabled={isUploading}>
                  Hủy
                </Button>
                <Button type="submit" disabled={isUploading}>
                  {isUploading ? stageLabel : 'Tạo báo cáo'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
